import { AntSkeleton, ColumnsType, Progress, Text } from '@agro-club/agroclub-shared'
import { ExportDealsTotals, ExportDealType } from 'modules/domain/export/types'
import formatNumber from 'modules/utils/numbers/formatNumber'
import formatPrice, { labelPrice } from 'modules/utils/numbers/formatPrice'
import { labelQuantity } from 'modules/utils/numbers/formatQuantity'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import styled from 'styled-components'
import { CompanyLink } from 'views/components/CompanyLink/CompanyLink'
import { CellRenders } from 'views/components/Table/renderers'
import DealRoutes from 'views/pages/Deal/routes'
import UserRoutes from 'views/pages/User/routes'
import { TableTextLink } from 'views/ui/TableTextLink/TableTextLink'

const SumPreload = () => <AntSkeleton.Avatar active size="small" style={{ width: 35, height: 20, borderRadius: 4 }} />

const QuantityColsSum: FC<{
  progress: Progress
  data: ExportDealsTotals | undefined
  field: keyof ExportDealsTotals
}> = ({ progress, data, field }) => {
  if (progress === Progress.WORK) {
    return <SumPreload />
  }
  return <Text typography="accentMedium">{formatNumber(data?.[field], true)}</Text>
}
const Links = styled.div`
  display: flex;
  flex-direction: column;
`

const useColumns = (
  quantityColsSumProgress: Progress,
  quantityColsSum: ExportDealsTotals | undefined,
): ColumnsType<ExportDealType> => {
  const { t } = useTranslation('export')

  return useMemo(
    () => [
      {
        title: t('dealSellerCompany'),
        dataIndex: 'id',
        width: 358,
        render: (_, row) => (
          <Links>
            <TableTextLink id="deal" tableItemId={row.id} to={generatePath(DealRoutes.Details, { id: row.id })}>
              {`ID ${row.id}`}
            </TableTextLink>
            <TableTextLink
              id="deal"
              tableItemId={row.owner.id}
              to={generatePath(UserRoutes.Details, { id: row.owner.id })}
            >
              {row.owner.full_name}
            </TableTextLink>
            <CompanyLink bodysize="bodyMedium" company={row.company} />
          </Links>
        ),
      },

      {
        title: (
          <div>
            <div>{labelQuantity(t('volume'))}</div>
            <QuantityColsSum progress={quantityColsSumProgress} data={quantityColsSum} field="quantity" />
          </div>
        ),
        dataIndex: 'quantity',
        align: 'right',
        width: 82,
        render: CellRenders.decimal,
      },
      {
        title: (
          <div>
            <div>{labelQuantity(t('uploaded'))}</div>
            <QuantityColsSum progress={quantityColsSumProgress} data={quantityColsSum} field="loaded_quantity" />
          </div>
        ),
        dataIndex: 'loaded_quantity',
        align: 'right',
        width: 104,
        render: CellRenders.decimal,
      },
      {
        title: (
          <div>
            <div>{labelQuantity(t('delivered'))}</div>
            <QuantityColsSum progress={quantityColsSumProgress} data={quantityColsSum} field="delivered_quantity" />
          </div>
        ),
        dataIndex: 'delivered_quantity',
        align: 'right',
        width: 90,
        render: CellRenders.decimal,
      },
      {
        title: (
          <div>
            <div>{labelQuantity(t('remaining'))}</div>
            <QuantityColsSum progress={quantityColsSumProgress} data={quantityColsSum} field="remaining_quantity" />
          </div>
        ),
        dataIndex: 'remaining_quantity',
        align: 'right',
        width: 98,
        render: CellRenders.decimal,
      },
      {
        title: labelPrice(t('price')),
        dataIndex: 'price_exw',
        align: 'right',
        width: 74,
        onHeaderCell: () => ({
          style: { verticalAlign: 'middle' },
        }),
        render: (price_exw) => formatPrice(price_exw),
      },
      {
        title: labelPrice(t('priceWithLogistic')),
        dataIndex: 'price_with_logistics',
        align: 'right',
        width: 134,
        onHeaderCell: () => ({
          style: { verticalAlign: 'middle' },
        }),
        render: CellRenders.price,
      },
    ],
    [quantityColsSum, quantityColsSumProgress, t],
  )
}

export default useColumns
